/**
 * Mixpanel Analytics Service
 * Centralized service for tracking events in Mixpanel
 */

import mixpanel from 'mixpanel-browser';
import {
  GenerateStartParams,
  ConvertParams,
  ResultClickParams,
  ReviewSubmitParams,
  ScreenClickParams,
  GenerateFinishParams,
  MixpanelModel,
  MixpanelAge,
  MixpanelBackground,
  MixpanelScreenClickButton,
  MixpanelType,
  MixpanelRatio,
  MixpanelScene,
  MixpanelGender,
  MixpanelHair,
} from '../types/mixpanel';
import { FilterState, /* RateLimitStatus */ } from '../types/api';

class MixpanelService {
  private isInitialized: boolean = false;

  /**
   * Initialize Mixpanel with token from environment variable
   */
  initialize(): void {
    if (this.isInitialized) {
      return;
    }

    try {
      const token = process.env.REACT_APP_MIXPANEL_TOKEN;

      if (!token) {
        console.warn('Mixpanel token not found. Analytics tracking will be disabled.');
        return;
      }

      mixpanel.init(token, {
        debug: false,
        track_pageview: false, // We'll handle page views manually if needed
        persistence: 'localStorage',
        record_sessions_percent: 100,
      });

      this.isInitialized = true;

      // Register URL params as super properties
      this.registerUrlParams();
    } catch (error) {
      console.error('Failed to initialize Mixpanel:', error);
      this.isInitialized = false;
    }
  }

  /**
   * Parse URL params (platform, planName, language, userId) and register them as Mixpanel super properties.
   * Also identifies the user if userId is provided.
   * Clears the params from the URL after capturing them.
   */
  private registerUrlParams(): void {
    const urlParams = new URLSearchParams(window.location.search);

    const platform = urlParams.get('platform');
    const planName = urlParams.get('planName');
    const language = urlParams.get('language');
    const userId = urlParams.get('userId');

    // Check if any tracking params exist
    const hasTrackingParams = platform || planName || language || userId;

    if (!hasTrackingParams) {
      return;
    }

    // Identify user if userId is provided
    if (userId) {
      mixpanel.identify(userId);
    }

    // Build super properties object (only include non-null values)
    const superProps: Record<string, string> = {};
    if (platform) superProps.platform = platform;
    if (planName) superProps.planName = planName;
    if (language) superProps.language = language;

    // Register as super properties
    mixpanel.register(superProps);

    // Clear tracking params from URL
    urlParams.delete('platform');
    urlParams.delete('planName');
    urlParams.delete('language');
    urlParams.delete('userId');

    // Build new URL without tracking params
    const newSearch = urlParams.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;

    // Replace URL without page reload
    window.history.replaceState({}, '', newUrl);
  }

  /**
   * Check if Mixpanel is initialized
   */
  private checkInitialized(): boolean {
    if (!this.isInitialized) {
      console.warn('Mixpanel not initialized. Event not tracked.');
      return false;
    }
    return true;
  }

  /**
   * Track an event safely with error handling
   */
  private track(eventName: string, properties?: Record<string, any>): void {
    if (!this.checkInitialized()) {
      return;
    }

    try {
      mixpanel.track(eventName, properties);
    } catch (error) {
      console.error(`Failed to track event "${eventName}":`, error);
      // Don't throw - tracking errors shouldn't break the app
    }
  }

  /**
   * Normalize model value from internal format to Mixpanel format
   */
  private normalizeModel(model: string | undefined): MixpanelModel {
    const modelMap: Record<string, MixpanelModel> = {
      'flux-dev': 'flux',
      'seedream-4': 'seedream',
      'nano-banana': 'nano-banana',
    };

    if (!model) return 'flux';
    return modelMap[model] || 'flux';
  }

  /**
   * Normalize age value from internal format to Mixpanel format
   */
  private normalizeAge(age: string): MixpanelAge {
    const ageMap: Record<string, MixpanelAge> = {
      'under20': '<20',
      '20s': '20s',
      '30s': '30s',
      '40s': '40s',
      '50s': '50s',
      '60s': '60s',
      '60plus': '60+',
      'any': 'any',
    };

    return ageMap[age] || 'any';
  }

  /**
   * Normalize scene value from internal format to Mixpanel format
   */
  private normalizeScene(scene: string): 'headshot' | 'mid-shot' | 'full-body' {
    const sceneMap: Record<string, 'headshot' | 'mid-shot' | 'full-body'> = {
      'headshot': 'headshot',
      'mid-closeup': 'mid-shot',
      'full-body': 'full-body',
    };

    return sceneMap[scene] || 'headshot';
  }

  /**
   * Normalize background value from internal format to Mixpanel format
   */
  private normalizeBackground(background: string): MixpanelBackground {
    const backgroundMap: Record<string, MixpanelBackground> = {
      'Green Screen': 'green_screen',
      'Office Desk': 'office_desk',
      'Interview setup with mic': 'interview',
      'Car interior': 'car',
      'Holding laptop': 'holding_laptop',
      'Coffee shop': 'coffee_shop',
      'Home Office': 'home_office',
      'any': 'any',
    };

    return backgroundMap[background] || 'any';
  }

  /**
   * Extract generation parameters from filters and other state
   * Returns common generation fields (model, type, ratio, scene, age, gender, hair, background)
   */
  private getGenerationParams(filters: FilterState): {
    model: MixpanelModel;
    type: MixpanelType;
    ratio: MixpanelRatio;
    scene: MixpanelScene;
    age: MixpanelAge;
    gender: MixpanelGender;
    hair: MixpanelHair;
    background: MixpanelBackground;
  } {
    // Normalize hair array to string for Mixpanel (use first value or 'any')
    const hairValue = Array.isArray(filters.hair) 
      ? (filters.hair.length > 0 ? filters.hair[0] : 'any')
      : filters.hair;
    
    return {
      model: this.normalizeModel(filters.model),
      type: filters.style.type as 'photorealistic' | 'cartoon',
      ratio: filters.style.ratio as 'vertical' | 'horizontal',
      scene: this.normalizeScene(filters.style.scene),
      age: this.normalizeAge(filters.age),
      gender: filters.gender as any,
      hair: (hairValue as MixpanelHair) || 'any',
      background: this.normalizeBackground(filters.background),
    };
  }

  /**
   * Track generate-start event
   */
  trackGenerateStart(
    prompt: string,
    filters: FilterState,
    hasReference: boolean,
    hasResults: boolean
  ): void {
    const params: GenerateStartParams = {
      prompt,
      hasReference,
      hasResults,
      ...this.getGenerationParams(filters),
    };

    this.track('tool-prompt2avatar-generate-start', params);
  }

  /**
   * Track convert event
   */
  trackConvert(
    trigger: 'stock-avatar' | 'credits',
    planDuration?: 'monthly' | 'yearly'
  ): void {
    const params: ConvertParams = {
      trigger,
      ...(trigger === 'stock-avatar' && planDuration ? { planDuration } : {}),
    };
    this.track('tool-prompt2avatar-convert', params);
  }

  /**
   * Track result-click event
   */
  trackResultClick(
    button: ResultClickParams['button'],
    prompt: string,
    filters: FilterState
  ): void {
    const params: ResultClickParams = {
      prompt,
      button,
      ...this.getGenerationParams(filters),
    };

    this.track('tool-prompt2avatar-result-click', params);
  }

  /**
   * Track review-submit event
   */
  trackReviewSubmit(
    name: string,
    email: string,
    prompt: string,
    filters: FilterState
  ): void {
    const params: ReviewSubmitParams = {
      prompt,
      name,
      email,
      ...this.getGenerationParams(filters),
    };

    this.track('tool-prompt2avatar-review-submit', params);
  }

  /**
   * Track screen-click event
   */
  trackScreenClick(
    button: MixpanelScreenClickButton,
    credits: number,
    hasResults: boolean
  ): void {
    const params: ScreenClickParams = {
      button,
      credits,
      hasResults,
    };

    this.track('tool-prompt2avatar-screen-click', params);
  }

  /**
   * Track generate-finish event
   */
  trackGenerateFinish(
    prompt: string,
    filters: FilterState,
    hasReference: boolean,
    hasResults: boolean
  ): void {
    const params: GenerateFinishParams = {
      prompt,
      hasReference,
      hasResults,
      ...this.getGenerationParams(filters),
    };

    // Note: Using the exact event name as specified (with typo)
    this.track('tool-prompt2avatart-generate-finish', params);
  }
}

// Singleton instance
export const mixpanelService = new MixpanelService();

